// Debug utility to help identify React errors
// Add this to your index.js or a separate debug.js file

class ReactErrorDebugger {
  constructor() {
    this.errors = [];
    this.setupGlobalErrorHandlers();
    this.setupReactDevTools();
    this.monitorComponents();
  }

  setupGlobalErrorHandlers() {
    // Catch all unhandled errors
    window.addEventListener('error', (event) => {
      const errorData = {
        type: 'javascript',
        message: event.error?.message || event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: new Date().toISOString()
      };
      
      this.logError('Global Error Handler', errorData);
      this.errors.push(errorData);
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const errorData = {
        type: 'promise_rejection',
        reason: event.reason,
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        timestamp: new Date().toISOString()
      };
      
      this.logError('Unhandled Promise Rejection', errorData);
      this.errors.push(errorData);
    });
  }

  setupReactDevTools() {
    // Hook into React DevTools if available
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
      
      // Monitor fiber errors
      const originalOnCommitFiberRoot = hook.onCommitFiberRoot;
      hook.onCommitFiberRoot = (id, root, ...args) => {
        try {
          // Check for errors in fiber tree
          this.checkFiberForErrors(root.current);
          return originalOnCommitFiberRoot?.(id, root, ...args);
        } catch (error) {
          this.logError('React DevTools Hook Error', error);
          return originalOnCommitFiberRoot?.(id, root, ...args);
        }
      };
    }
  }

  checkFiberForErrors(fiber) {
    if (!fiber) return;

    // Check current fiber for errors
    if (fiber.effectTag && (fiber.effectTag & 128) !== 0) { // Error effect tag
      this.logError('Fiber Error Detected', {
        componentName: this.getFiberComponentName(fiber),
        fiberType: fiber.type,
        props: fiber.memoizedProps,
        state: fiber.memoizedState
      });
    }

    // Check children recursively
    let child = fiber.child;
    while (child) {
      this.checkFiberForErrors(child);
      child = child.sibling;
    }
  }

  getFiberComponentName(fiber) {
    if (!fiber) return 'Unknown';
    
    if (fiber.type) {
      if (typeof fiber.type === 'string') return fiber.type;
      if (fiber.type.displayName) return fiber.type.displayName;
      if (fiber.type.name) return fiber.type.name;
    }
    
    return 'Anonymous';
  }

  monitorComponents() {
    // Override console methods to catch React warnings
    const originalConsoleError = console.warn;
    const originalConsoleWarn = console.warn;
    
    console.warn = (...args) => {
      const message = args.join(' ');
      
      // Detect React-specific errors
      if (message.includes('Warning:') || message.includes('React') || message.includes('component')) {
        this.logError('React Console Error', {
          message,
          args,
          stack: new Error().stack
        });
      }
      
      return originalConsoleError.apply(console, args);
    };

    console.warn = (...args) => {
      const message = args.join(' ');
      
      if (message.includes('Warning:') || message.includes('React')) {
        this.logError('React Console Warning', {
          message,
          args,
          stack: new Error().stack
        });
      }
      
      return originalConsoleWarn.apply(console, args);
    };
  }

  logError(source, errorData) {
    console.group(`🔍 [ReactErrorDebugger] ${source}`);
    console.warn('Error Data:', errorData);
    
    // Try to identify the source component
    if (errorData.stack) {
      const componentInfo = this.extractComponentFromStack(errorData.stack);
      if (componentInfo) {
        console.info('Likely Component:', componentInfo);
      }
    }
    
    // Check Redux state if available
    if (window.__REDUX_STORE__) {
      try {
        const state = window.__REDUX_STORE__.getState();
        console.info('Current Redux State:', {
          auth: state.auth,
          router: state.router || 'N/A'
        });
      } catch (e) {
        console.warn('Could not access Redux state:', e);
      }
    }
    
    console.groupEnd();
  }

  extractComponentFromStack(stack) {
    if (!stack) return null;
    
    const lines = stack.split('\n');
    
    // Look for React component patterns in the stack
    for (const line of lines) {
      // Look for patterns like "at ComponentName" or "ComponentName@"
      const reactComponentMatch = line.match(/at\s+([A-Z][a-zA-Z0-9]*)/);
      if (reactComponentMatch) {
        return reactComponentMatch[1];
      }
      
      // Look for file patterns that might contain components
      const fileMatch = line.match(/\/([A-Z][a-zA-Z0-9]*)\.(js|jsx|ts|tsx)/);
      if (fileMatch) {
        return fileMatch[1];
      }
    }
    
    return null;
  }

  // Public methods for debugging
  getErrorHistory() {
    return [...this.errors];
  }

  clearErrorHistory() {
    this.errors = [];
  }

  generateDebugReport() {
    return {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errors: this.errors,
      
      // React-specific info
      reactVersion: this.getReactVersion(),
      devToolsAvailable: !!window.__REACT_DEVTOOLS_GLOBAL_HOOK__,
      
      // Redux info
      reduxAvailable: !!window.__REDUX_STORE__,
      reduxState: this.getReduxState(),
      
      // Performance info
      performance: this.getPerformanceInfo(),
      
      // Memory info
      memory: this.getMemoryInfo()
    };
  }

  getReactVersion() {
    try {
      // Try to get React version from various sources
      if (window.React) {
        return window.React.version;
      }
      
      // Try to find in loaded modules
      const reactScript = Array.from(document.scripts).find(script => 
        script.src.includes('react') || script.textContent?.includes('React')
      );
      
      if (reactScript) {
        return 'Found but version unknown';
      }
      
      return 'Not found';
    } catch (e) {
      return 'Error detecting version';
    }
  }

  getReduxState() {
    try {
      if (window.__REDUX_STORE__) {
        const state = window.__REDUX_STORE__.getState();
        return {
          auth: state.auth,
          profile: state.profile,
          router: state.router
        };
      }
      return 'Redux not available';
    } catch (e) {
      return `Error accessing Redux: ${e.message}`;
    }
  }

  getPerformanceInfo() {
    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      return {
        loadComplete: navigation?.loadEventEnd - navigation?.navigationStart,
        domReady: navigation?.domContentLoadedEventEnd - navigation?.navigationStart,
        firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
        firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime
      };
    } catch (e) {
      return `Error getting performance info: ${e.message}`;
    }
  }

  getMemoryInfo() {
    try {
      if (performance.memory) {
        return {
          used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
          total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
          limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
        };
      }
      return 'Memory info not available';
    } catch (e) {
      return `Error getting memory info: ${e.message}`;
    }
  }

  // Add debug commands to window for manual testing
  exposeDebugCommands() {
    window.reactDebugger = {
      getErrors: () => this.getErrorHistory(),
      clearErrors: () => this.clearErrorHistory(),
      generateReport: () => this.generateDebugReport(),
      triggerTestError: () => {
        throw new Error('Test error triggered by debugger');
      },
      triggerTestPromiseRejection: () => {
        Promise.reject(new Error('Test promise rejection triggered by debugger'));
      }
    };
    
    //console.log('%c🔧 React Debugger Commands Available:', 'color: blue; font-weight: bold;');
    //console.log('- reactDebugger.getErrors() - Get error history');
    //console.log('- reactDebugger.clearErrors() - Clear error history');
    //console.log('- reactDebugger.generateReport() - Generate full debug report');
    //console.log('- reactDebugger.triggerTestError() - Trigger test error');
    //console.log('- reactDebugger.triggerTestPromiseRejection() - Trigger test promise rejection');
  }
}

// Initialize debugger
const reactErrorDebugger = new ReactErrorDebugger();

// Expose debug commands in development
if (process.env.NODE_ENV === 'development') {
  reactErrorDebugger.exposeDebugCommands();
}

export default reactErrorDebugger;